import React, {useState, useEffect} from "react";
import {Projects} from "../types";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    TextField,
    Tooltip,
    Typography
} from "@mui/material";
import 'dayjs/locale/it';
import {ProjectAPI} from "../services/API/ProjectAPI";
import {useTranslation} from "react-i18next";
import {Translate as TranslateIcon} from "@mui/icons-material";
import {ProjectTranDTO} from "../types/DTOs/Translation/ProjectTranDTO";
import {Translate} from "../utils/Translate";
import {TranslationAPI} from "../services/API/TranslationAPI";
import {SectionTypes} from "../utils/enums";

// ─────────────────────────────────────────────────────────────────────────────
const DEFAULT_LANG = 'original';

// ─────────────────────────────────────────────────────────────────────────────
// PROPS
// ─────────────────────────────────────────────────────────────────────────────
interface EditProjectProps {
    item: Projects | null;
    onSave: () => void;
    onCancel: () => void;
    open: boolean;
    onClose: () => void;
    /** Lingue già tradotte per questo elemento */
    translatedLanguages?: string[];
}

// ─────────────────────────────────────────────────────────────────────────────
// API
// ─────────────────────────────────────────────────────────────────────────────
const fetchAvailableLanguages = async (): Promise<{ code: string; i18nKey: string }[]> => {
    return Translate.LanguageForCombo();
};

const fetchProjectTranslation = async (
    projectId: string,
    languageCode: string
): Promise<ProjectTranDTO | null | undefined> => {
    const dataTran = await TranslationAPI.Instance<TranslationAPI>().getTranslatedItem(projectId, SectionTypes.PROJECT, languageCode);
    if (dataTran === null || dataTran === undefined) return null;
    return dataTran as ProjectTranDTO;
};

const saveProjectTranslation = async (data: ProjectTranDTO): Promise<void> => {
    await TranslationAPI.Instance<TranslationAPI>().saveTranslateItem(data, SectionTypes.PROJECT);
};

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENTE
// ─────────────────────────────────────────────────────────────────────────────
export const EditProject: React.FC<EditProjectProps> = ({
                                                            item,
                                                            onSave,
                                                            onCancel,
                                                            open,
                                                            onClose,
                                                            translatedLanguages = [],
                                                        }) => {
    // ── Stato originale ──
    const [project, setProject] = useState<Projects>({
        id: '',
        userId: '',
        projectName: '',
        description: '',
        technologies: '',
        url: '',
        createdAt: '',
        updatedAt: ''
    });

    // ── Stato traduzione ──
    const [availableLanguages, setAvailableLanguages] = useState<{ code: string; i18nKey: string }[]>([]);
    const [selectedLang, setSelectedLang] = useState<string>(DEFAULT_LANG);
    const [translation, setTranslation] = useState<ProjectTranDTO>({
        id: '',
        projectId: '',
        projectName: '',
        description: '',
    });
    const [translationLoading, setTranslationLoading] = useState(false);
    const [translationError, setTranslationError] = useState<string>('');
    const [saveLoading, setSaveLoading] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);

    const isTranslationMode = selectedLang !== DEFAULT_LANG;

    const { t } = useTranslation();

    // ─────────────────────────────────────────────────────────────────────────
    // EFFECTS
    // ─────────────────────────────────────────────────────────────────────────
    useEffect(() => {
        if (open) {
            setSelectedLang(DEFAULT_LANG);
            setTranslationError('');
            setSaveSuccess(false);

            if (item === null) {
                setProjectDefault();
            } else {
                setProject(item);
            }

            fetchAvailableLanguages()
                .then(setAvailableLanguages)
                .catch(() => {});
        }
    }, [item, open]);

    useEffect(() => {
        if (!isTranslationMode || !project.id) return;

        setTranslationLoading(true);
        setTranslationError('');
        setSaveSuccess(false);

        fetchProjectTranslation(project.id, selectedLang)
            .then(data => {
                if (data) {
                    setTranslation(data);
                } else {
                    setTranslation({
                        id: '',
                        projectId: project.id,
                        projectName: '',
                        description: '',
                    });
                }
            })
            .catch(() => setTranslationError(t('translation.errorGeneric')))
            .finally(() => setTranslationLoading(false));
    }, [selectedLang]);

    // ─────────────────────────────────────────────────────────────────────────
    // HANDLERS
    // ─────────────────────────────────────────────────────────────────────────
    const setProjectDefault = () => {
        setProject({
            id: '', userId: '', projectName: '', description: '',
            technologies: '', url: '', createdAt: '', updatedAt: ''
        });
    };

    const handleInputChange = (event: any) => {
        const { name, value } = event.target;
        setProject(prev => ({ ...prev, [name]: value }));
    };

    const handleTranslationInputChange = (event: any) => {
        const { name, value } = event.target;
        setTranslation(prev => ({ ...prev, [name]: value }));
    };

    const handleLangChange = (e: SelectChangeEvent) => {
        setSelectedLang(e.target.value);
        setSaveSuccess(false);
    };

    const normalizeUrl = (url: string | undefined): string => {
        if (!url || url.trim() === '') return url ?? '';
        const trimmed = url.trim();
        if (!/^https?:\/\//i.test(trimmed)) return `https://${trimmed}`;
        return trimmed;
    };

    const handleUrlBlur = () => {
        setProject(prev => ({ ...prev, url: normalizeUrl(prev.url) }));
    };

    // Salva originale
    const handleSaveItem = async () => {
        try {
            const normalizedUrl = normalizeUrl(project.url);

            if (!project.id) {
                await ProjectAPI.Instance<ProjectAPI>().createProjects({
                    projectName: project.projectName,
                    description: project.description,
                    technologies: project.technologies,
                    url: normalizedUrl ? normalizedUrl : null
                });
            } else {
                await ProjectAPI.Instance<ProjectAPI>().updateProjects(project.id, {
                    projectName: project.projectName,
                    description: project.description,
                    technologies: project.technologies,
                    url: normalizedUrl ? normalizedUrl : null
                });
            }

            setProjectDefault();
            onSave();
        } catch (error) {
            console.error('Error saving project:', error);
        }
    };

    // Salva traduzione (non chiude la dialog)
    const handleSaveTranslation = async () => {
        setSaveLoading(true);
        setSaveSuccess(false);
        setTranslationError('');
        try {
            await saveProjectTranslation({
                ...translation,
                projectId: project.id,
            });
            setSaveSuccess(true);
        } catch {
            setTranslationError(t('translation.errorGeneric'));
        } finally {
            setSaveLoading(false);
        }
    };

    // ─────────────────────────────────────────────────────────────────────────
    // RENDER
    // ─────────────────────────────────────────────────────────────────────────
    const langLabel = availableLanguages.find(l => l.code === selectedLang)?.i18nKey ?? '';

    return (
        <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>

            {/* ── Titolo ── */}
            <DialogTitle sx={{ pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>{t('projectDialog.title')}</span>
                    {isTranslationMode && (
                        <Chip
                            icon={<TranslateIcon />}
                            label={t(langLabel)}
                            color="primary"
                            size="small"
                            variant="outlined"
                        />
                    )}
                </Box>
            </DialogTitle>

            {/* ── Banda lingua ── */}
            {project.id && (
                <Box sx={{ px: 3, py: 1.5, bgcolor: 'action.hover', borderBottom: 1, borderColor: 'divider' }}>
                    <FormControl size="small" sx={{ minWidth: 220 }}>
                        <InputLabel>{t('translation.selectLanguageLabel')}</InputLabel>
                        <Select
                            value={selectedLang}
                            label={t('translation.selectLanguageLabel')}
                            onChange={handleLangChange}
                            startAdornment={<TranslateIcon sx={{ mr: 1, fontSize: '1rem', color: 'text.secondary' }} />}
                        >
                            <MenuItem value={DEFAULT_LANG}>
                                <em>{t('translation.originalLanguage')}</em>
                            </MenuItem>
                            <Divider />
                            {availableLanguages.map(lang => (
                                <MenuItem key={lang.code} value={lang.code}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {t(lang.i18nKey)}
                                        {translatedLanguages.includes(lang.code) && (
                                            <Chip
                                                label={t('translation.alreadyTranslatedChip')}
                                                size="small"
                                                color="success"
                                                variant="outlined"
                                                sx={{ fontSize: '0.65rem', height: 18 }}
                                            />
                                        )}
                                    </Box>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {isTranslationMode && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            {translatedLanguages.includes(selectedLang)
                                ? t('translation.editModeExisting')
                                : t('translation.editModeNew')}
                        </Typography>
                    )}
                </Box>
            )}

            <DialogContent sx={{ pt: 2 }}>

                {translationLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                        <CircularProgress size={28} />
                    </Box>
                )}

                {translationError && (
                    <Alert severity="error" sx={{ mb: 2 }}>{translationError}</Alert>
                )}

                {saveSuccess && (
                    <Alert severity="success" sx={{ mb: 2 }}>{t('translation.saveSuccess')}</Alert>
                )}

                {!translationLoading && (
                    <>
                        {/* ── Nome progetto — traducibile ── */}
                        <TextField
                            name="projectName"
                            margin="dense"
                            label={t('projectDialog.name')}
                            fullWidth
                            variant="outlined"
                            value={isTranslationMode ? translation.projectName : project.projectName}
                            onChange={isTranslationMode ? handleTranslationInputChange : handleInputChange}
                            sx={{ mb: 2 }}
                        />

                        {/* ── Descrizione — traducibile ── */}
                        <TextField
                            name="description"
                            margin="dense"
                            label={t('projectDialog.description')}
                            fullWidth
                            variant="outlined"
                            value={isTranslationMode ? translation.description : project.description}
                            onChange={isTranslationMode ? handleTranslationInputChange : handleInputChange}
                            multiline
                            rows={3}
                            sx={{ mb: 2 }}
                        />

                        {/* ── Tecnologie — NON traducibile ── */}
                        <Tooltip
                            title={isTranslationMode ? t('translation.fieldNotTranslatable') : ''}
                            placement="top-start"
                        >
                            <TextField
                                name="technologies"
                                margin="dense"
                                label={t('projectDialog.technologies')}
                                fullWidth
                                variant="outlined"
                                value={project.technologies}
                                onChange={handleInputChange}
                                disabled={isTranslationMode}
                                multiline
                                rows={3}
                                sx={{ mb: 2 }}
                            />
                        </Tooltip>

                        {/* ── URL — NON traducibile ── */}
                        <Tooltip
                            title={isTranslationMode ? t('translation.fieldNotTranslatable') : ''}
                            placement="top-start"
                        >
                            <TextField
                                name="url"
                                margin="dense"
                                label={t('projectDialog.url')}
                                fullWidth
                                variant="outlined"
                                value={project.url}
                                onChange={handleInputChange}
                                onBlur={handleUrlBlur}
                                disabled={isTranslationMode}
                                sx={{ mb: 2 }}
                            />
                        </Tooltip>
                    </>
                )}

            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                <Button onClick={onClose} variant="outlined" color="inherit">
                    {t('standardUse.undoBtn')}
                </Button>

                {isTranslationMode ? (
                    <Button
                        onClick={handleSaveTranslation}
                        variant="contained"
                        color="primary"
                        disabled={saveLoading || translationLoading}
                        startIcon={saveLoading
                            ? <CircularProgress size={16} color="inherit" />
                            : <TranslateIcon />
                        }
                    >
                        {saveLoading
                            ? t('translation.savingButton')
                            : t('translation.saveTranslationButton', { lang: t(langLabel) })}
                    </Button>
                ) : (
                    <Button onClick={handleSaveItem} variant="contained">
                        {t('standardUse.saveBtn')}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};